import dayjs from "@punchup/utils/dayjs";

export interface AutomationsUsage {
  /** Credits spent from the Automations pool in the requested window. */
  usedCredits: number;
  /** Live pool balance; zero is what pauses sends. */
  automationsBalance: number;
}

/**
 * The monthly window containing `now`, stepping monthly from the period start.
 * Annual plans keep a monthly pool, so their usage resets every month too.
 */
export function monthlyUsageWindow(
  startIso: string,
  endIso: string,
  now: string | Date = new Date()
): { start: string; end: string } {
  const periodStart = dayjs.utc(startIso);
  const periodEnd = dayjs.utc(endIso);
  const at = dayjs.utc(now);

  let months = Math.max(0, at.diff(periodStart, "month"));
  // Step from the period start (not a prior step) so month-end days clamp once.
  let start = periodStart.add(months, "month");
  while (start.isAfter(at) && months > 0) {
    months -= 1;
    start = periodStart.add(months, "month");
  }
  const stepEnd = periodStart.add(months + 1, "month");
  const end = stepEnd.isAfter(periodEnd) ? periodEnd : stepEnd;
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Credits an immediate upgrade adds to the pool: the new allotment less the
 * unused share of the old one. The upgrade restarts the billing period, so
 * Stripe bills a full new month minus that unused time. Without a window the
 * whole old allotment counts as unused.
 */
export function proratedUpgradeTopUp(
  oldAllotment: number,
  newAllotment: number,
  window: { start: string; end: string } | null,
  now: string | Date = new Date()
): number {
  let remaining = 1;
  if (window) {
    const start = dayjs.utc(window.start).valueOf();
    const length = dayjs.utc(window.end).valueOf() - start;
    const elapsed = dayjs.utc(now).valueOf() - start;
    remaining = length > 0 ? Math.min(1, Math.max(0, 1 - elapsed / length)) : 0;
  }
  return Math.max(0, newAllotment - Math.round(oldAllotment * remaining));
}
